import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { isValid, parseISO } from 'date-fns';
import {
  Button,
  CalendarProps,
  CloseIcon,
  ConfiguredCalendar,
  Dialog,
  InputText,
  InputTextarea,
  Toast,
} from '@repo/atomic-components';
import { RefObject } from 'react';

/**
 * The shape of the modal's initial data. Note this is *not* `AdditionalInfo`:
 * `additionalReference` maps to the top-level `info.gRef`, while the other
 * four live on `additionalInfo`. The parent (`ActionsPanel`) is responsible
 * for combining them on the way in and splitting them on save.
 */
export interface DetailsDialogInitialData {
  name: string | null;
  dateOfBirth: string | null;
  additionalReference: string | null;
  preExistingConditions: string | null;
  surgicalProcedures: string | null;
}

export interface DetailsDialogFormValues {
  name: string;
  dateOfBirth: Date | null;
  additionalReference: string;
  preExistingConditions: string;
  surgicalProcedures: string;
}

interface DetailsDialogProps {
  initialData: DetailsDialogInitialData | null;
  isSaving: boolean;
  onClose: () => void;
  /** true closes the dialog, false shows the error toast, null keeps it open for a failure already explained */
  onSave: (values: DetailsDialogFormValues) => Promise<boolean | null> | boolean | null;
  toastRef: RefObject<Toast | null>;
}

const toFormState = (data: DetailsDialogInitialData | null): DetailsDialogFormValues => {
  const parsedDob = data?.dateOfBirth ? parseISO(data.dateOfBirth) : null;
  return {
    name: data?.name ?? '',
    dateOfBirth: parsedDob && isValid(parsedDob) ? parsedDob : null,
    additionalReference: data?.additionalReference ?? '',
    preExistingConditions: data?.preExistingConditions ?? '',
    surgicalProcedures: data?.surgicalProcedures ?? '',
  };
};

export const DetailsDialog = ({ initialData, isSaving, onClose, onSave, toastRef }: DetailsDialogProps) => {
  const [values, setValues] = useState<DetailsDialogFormValues>(() => toFormState(initialData));
  // Track whether the user has started editing. Once they have, we stop
  // syncing from `initialData` so a background refetch can't clobber their
  // in-flight edits. The dialog is conditionally rendered by the parent, so
  // this ref resets naturally each time the modal is opened.
  const isDirtyRef = useRef(false);

  // If the upstream data changes while the dialog is open (e.g. a re-fetch
  // landed before the user typed anything) keep the form in sync — but only
  // while the form is still pristine.
  useEffect(() => {
    if (isDirtyRef.current) return;
    setValues(toFormState(initialData));
  }, [initialData]);

  const handleChange = <K extends keyof DetailsDialogFormValues>(key: K, value: DetailsDialogFormValues[K]) => {
    isDirtyRef.current = true;
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveClick = async () => {
    const ok = await onSave(values);
    if (ok) {
      onClose();
    } else if (ok === false) {
      toastRef.current?.show({
        severity: 'error',
        detail: 'Failed to save details. Please try again.',
        life: 5000,
      });
    }
  };

  return (
    <Dialog
      visible={true}
      onHide={onClose}
      style={{
        width: '90vw',
        maxWidth: '760px',
        borderRadius: '8px',
        border: '1px solid #e1e1e0',
        overflow: 'hidden',
      }}
      contentClassName="!p-0"
      headerClassName="!hidden"
    >
      <div className="border-fletchers-gray-110 flex items-center justify-between border-b px-[24px] py-[16px]">
        <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Details</div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="cursor-pointer rounded p-1 focus:outline-none"
        >
          <CloseIcon width={12} height={12} />
        </button>
      </div>

      <div className="flex flex-col gap-4 px-[24px] py-[16px] text-[14px]">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h4 className="mb-2 text-sm font-semibold">Name</h4>
            <InputText
              value={values.name}
              onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange('name', event.target.value)}
              className="!h-[32px] w-full !text-[14px]"
            />
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold">Date of birth</h4>
            <ConfiguredCalendar
              showOnFocus
              value={values.dateOfBirth}
              onChange={(event: CalendarProps<'single', Date | (Date | null)[]>) =>
                handleChange('dateOfBirth', (event.value as Date) ?? null)
              }
              className="w-full rounded-sm bg-white [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
            />
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold">Additional reference</h4>
            <InputText
              value={values.additionalReference}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleChange('additionalReference', event.target.value)
              }
              className="!h-[32px] w-full !text-[14px]"
            />
          </div>
        </div>

        <div>
          <h4 className="mb-2 text-sm font-semibold">Pre-existing conditions</h4>
          <InputTextarea
            value={values.preExistingConditions}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              handleChange('preExistingConditions', event.target.value)
            }
            rows={6}
            className="w-full resize-none !text-[14px]"
          />
        </div>

        <div>
          <h4 className="mb-2 text-sm font-semibold">Surgical procedures</h4>
          <InputTextarea
            value={values.surgicalProcedures}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              handleChange('surgicalProcedures', event.target.value)
            }
            rows={6}
            className="w-full resize-none !text-[14px]"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-[24px] pb-[24px]">
        <Button outlined className="!h-[32px] [&]:bg-white" severity="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button className="!h-[32px]" onClick={handleSaveClick} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Dialog>
  );
};
